import { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import {
  SearchableSelect,
  type SearchableSelectOption
} from '@/components/ui/searchable-select';
import { showToast } from '@/components/ui/show-toast';
import { useGetAllOrganisations } from '@/services/organisations/queries';
import { useCreateInvitation } from '@/services/invitations/queries';
import type { Invitation } from '@/services/types';

const inviteSchema = z.object({
  email: z.string().email('Please enter a valid email address'),
  organisationId: z.string().min(1, 'Organisation is required')
});

type InviteFormData = z.infer<typeof inviteSchema>;

interface OrganisationInviteModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
  defaultEmail?: string;
  defaultOrganisationId?: string;
  defaultOrganisationName?: string;
  lockOrganisation?: boolean;
}

export function OrganisationInviteModal({
  open,
  onOpenChange,
  onSuccess,
  defaultEmail = '',
  defaultOrganisationId = '',
  defaultOrganisationName = '',
  lockOrganisation = false
}: OrganisationInviteModalProps) {
  const [orgSearch, setOrgSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [selectedOption, setSelectedOption] =
    useState<SearchableSelectOption | null>(null);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(orgSearch);
    }, 300);
    return () => clearTimeout(timer);
  }, [orgSearch]);

  const form = useForm<InviteFormData>({
    resolver: zodResolver(inviteSchema),
    defaultValues: {
      email: '',
      organisationId: ''
    }
  });

  useEffect(() => {
    if (!open) return;

    const organisationOption =
      defaultOrganisationId && defaultOrganisationName
        ? { id: defaultOrganisationId, label: defaultOrganisationName }
        : null;

    setSelectedOption(organisationOption);
    setOrgSearch('');
    setDebouncedSearch('');
    form.reset({
      email: defaultEmail,
      organisationId: defaultOrganisationId
    });
  }, [
    open,
    defaultEmail,
    defaultOrganisationId,
    defaultOrganisationName,
    form
  ]);

  const { data: organisationsData, isLoading: isLoadingOrgs } =
    useGetAllOrganisations(
      {
        page: 1,
        limit: 50,
        ...(debouncedSearch && { search: debouncedSearch })
      },
      { enabled: open }
    );

  const organisationOptions = useMemo(() => {
    const orgs = organisationsData?.data || [];
    const options = orgs.map((org) => ({
      id: org.id,
      label: org.name
    }));

    if (
      selectedOption &&
      !options.some((option) => option.id === selectedOption.id)
    ) {
      options.unshift(selectedOption);
    }

    return options;
  }, [organisationsData, selectedOption]);

  const { mutate: createInvitation, isPending: isSubmitting } =
    useCreateInvitation(
      () => {
        showToast('success', 'Invitation sent successfully');
        form.reset({ email: '', organisationId: '' });
        setOrgSearch('');
        setDebouncedSearch('');
        setSelectedOption(null);
        onOpenChange(false);
        onSuccess?.();
      },
      (error) => {
        showToast(
          'error',
          error?.response?.data?.message || 'Failed to send invitation'
        );
      }
    );

  const handleSubmit = (data: InviteFormData) => {
    const invitationData: Invitation = {
      email: data.email,
      context: 'organisation',
      organisationId: data.organisationId,
      role: 'org_admin'
    };

    createInvitation(invitationData);
  };

  const handleClose = () => {
    form.reset({ email: '', organisationId: '' });
    setOrgSearch('');
    setDebouncedSearch('');
    setSelectedOption(null);
    onOpenChange(false);
  };

  const handleSearchChange = useCallback((search: string) => {
    setOrgSearch(search);
  }, []);

  const handleOrganisationChange = useCallback(
    (organisationId: string) => {
      const option =
        organisationOptions.find((org) => org.id === organisationId) || null;
      setSelectedOption(option);
      form.setValue('organisationId', organisationId, { shouldValidate: true });
    },
    [organisationOptions, form]
  );

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && handleClose()}>
      <DialogContent className='sm:max-w-[425px]'>
        <DialogHeader>
          <DialogTitle>Invite</DialogTitle>
          <DialogDescription>
            Invite a user to join an organisation by email.
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleSubmit)} className='space-y-4'>
            <FormField
              control={form.control}
              name='email'
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Email Address</FormLabel>
                  <FormControl>
                    <Input
                      placeholder='user@example.com'
                      type='email'
                      disabled={isSubmitting}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name='organisationId'
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Organisation</FormLabel>
                  <FormControl>
                    <SearchableSelect
                      options={organisationOptions}
                      value={field.value}
                      onChange={handleOrganisationChange}
                      onSearchChange={handleSearchChange}
                      placeholder='Select an organisation'
                      searchPlaceholder='Search organisations'
                      emptyMessage='No organisations found'
                      isLoading={isLoadingOrgs}
                      disabled={lockOrganisation || isSubmitting}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <DialogFooter>
              <Button
                type='button'
                variant='outline'
                onClick={handleClose}
                disabled={isSubmitting}
              >
                Cancel
              </Button>
              <Button
                type='submit'
                disabled={isSubmitting}
                isLoading={isSubmitting}
              >
                Send Invite
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
